import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Drawer,
  Icon,
  IconButton,
  InputAdornment,
  TextField,
  Theme,
  Typography,
} from "@outschool/backpack";
import {
  BlockScopedThreadsForSectionQuery,
  BlockScopedThreadsForSectionQueryVariables,
} from "@outschool/gql-frontend-generated";
import { faComment, faSearch, faTimes } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { dayjs } from "@outschool/time";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import {
  BlockScopedMessageDraft,
  BlockScopedOptimisticThreadCreatedPayload,
  BlockScopedThreadCreateFailedPayload,
} from "@outschool/ui-components-classroom";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import { useIsMobile } from "@outschool/ui-utils";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import {
  CurrentPostContext,
  PostType,
  useMessageTeacher,
} from "../../providers/MessageTeacherProvider";
import { BLOCK_SCOPED_THREADS_FOR_SECTION_QUERY } from "../../queries/BlockScopedThreadsForSectionQuery";
import { MESSAGE_TEACHER_POLL_INTERVAL_MS } from "./constants";
import NewThreadView from "./NewThreadView";
import ThreadDetailView from "./ThreadDetailView";
import ThreadListItem from "./ThreadListItem";
import type { MessageData, ThreadViewContext } from "./types";
import {
  postTargetFromPendingPost,
  postTitleFromThreadOrOptimistic,
} from "./utils";

interface PendingPost {
  postUid: string;
  postType: PostType;
  postTitle: string;
  source: "post" | "emptyState";
}

interface ComposerRestoreState {
  draft: BlockScopedMessageDraft;
  error: unknown;
}

interface OptimisticThreadState {
  tempThreadUid: string;
  resolvedThreadUid: string | null;
  pendingPost: PendingPost;
  messages: MessageData[];
}

interface MessageTeacherButtonProps {
  sectionUid: string;
}

function contextToPendingPost(
  context: CurrentPostContext,
  source: PendingPost["source"],
): PendingPost {
  return {
    postUid: context.postUid,
    postType: context.postType,
    postTitle: context.postTitle,
    source,
  };
}

function toMessageData(
  message: BlockScopedOptimisticThreadCreatedPayload["optimisticMessage"],
): MessageData {
  return {
    uid: message.uid,
    sentAt: message.sentAt,
    sender: {
      uid: message.sender.uid,
      name: message.sender.name,
      photo: message.sender.photo,
      isLearner: message.sender.isLearner,
    },
    messageContent: {
      uid: message.messageContent.uid,
      text: message.messageContent.text,
      attachments: message.messageContent.attachments,
      video: message.messageContent.video,
    },
  };
}

function useStateWithRef<T>(initialState: T) {
  const [state, setState] = useState<T>(initialState);
  const stateRef = useRef(state);

  const setStateAndRef = useCallback((next: React.SetStateAction<T>) => {
    setState(previousState => {
      const resolvedState =
        typeof next === "function"
          ? (next as (prevState: T) => T)(previousState)
          : next;
      stateRef.current = resolvedState;
      return resolvedState;
    });
  }, []);

  return [state, setStateAndRef, stateRef] as const;
}

export default function MessageTeacherButton({
  sectionUid,
}: MessageTeacherButtonProps) {
  const { t } = useTranslation("learnerApp\\MessageTeacherButton");
  const isMobile = useIsMobile();
  const currentLearner = useCurrentLearner();
  const { registerOpenForPost, currentPostContext } = useMessageTeacher();
  const [open, setOpen] = useState(false);
  const [selectedThreadUid, setSelectedThreadUid, selectedThreadUidRef] =
    useStateWithRef<string | null>(null);
  const [pendingPost, setPendingPost, pendingPostRef] =
    useStateWithRef<PendingPost | null>(null);
  const [composerRestoreState, setComposerRestoreState] =
    useState<ComposerRestoreState | null>(null);
  const [optimisticThread, setOptimisticThread, optimisticThreadRef] =
    useStateWithRef<OptimisticThreadState | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const clearComposerFlowState = useCallback(() => {
    setComposerRestoreState(null);
    setOptimisticThread(null);
  }, [setOptimisticThread]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const {
    data,
    loading: threadsLoading,
    refetch,
  } = useQueryWithPreviousData<
    BlockScopedThreadsForSectionQuery,
    BlockScopedThreadsForSectionQueryVariables
  >(BLOCK_SCOPED_THREADS_FOR_SECTION_QUERY, {
    variables: {
      sectionUid,
      searchQuery: debouncedSearch || undefined,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: MESSAGE_TEACHER_POLL_INTERVAL_MS,
  });

  const unreadCount = data?.section?.unreadBlockScopedThreadsCount ?? 0;
  const leader = data?.section?.leader;
  const threads = useMemo(
    () => data?.section?.blockScopedThreads ?? [],
    [data?.section?.blockScopedThreads],
  );
  const sectionEndTime = data?.section?.end_time ?? null;

  const openForPost = useCallback(
    (postUid: string, postType: PostType, postTitle: string) => {
      const match = threads.find(thread => {
        if (postType === "assignmentPost") {
          return thread.learningAssignmentClassPost?.uid === postUid;
        }
        return thread.classPost?.uid === postUid;
      });

      if (match) {
        setSelectedThreadUid(match.uid);
        setPendingPost(null);
        clearComposerFlowState();
      } else {
        clearComposerFlowState();
        setSelectedThreadUid(null);
        setPendingPost({ postUid, postType, postTitle, source: "post" });
      }
      setOpen(true);
    },
    [clearComposerFlowState, setPendingPost, setSelectedThreadUid, threads],
  );

  useEffect(() => {
    registerOpenForPost(openForPost);
  }, [registerOpenForPost, openForPost]);

  const handleClose = () => {
    setOpen(false);
    setSelectedThreadUid(null);
    setPendingPost(null);
    clearComposerFlowState();
    setSearchInput("");
    setDebouncedSearch("");
  };

  const handleThreadCreated = useCallback(
    (threadUid: string) => {
      const currentOptimisticThread = optimisticThreadRef.current;
      setPendingPost(null);
      setComposerRestoreState(null);
      if (!currentOptimisticThread) {
        setSelectedThreadUid(threadUid);
        refetch();
        return;
      }

      const isViewingTemporaryThread =
        selectedThreadUidRef.current === currentOptimisticThread.tempThreadUid;
      if (isViewingTemporaryThread) {
        setSelectedThreadUid(threadUid);
        setOptimisticThread(previous =>
          previous &&
          previous.tempThreadUid === currentOptimisticThread.tempThreadUid
            ? { ...previous, resolvedThreadUid: threadUid }
            : previous,
        );
      } else {
        setOptimisticThread(null);
      }

      refetch();
    },
    [
      optimisticThreadRef,
      refetch,
      selectedThreadUidRef,
      setPendingPost,
      setSelectedThreadUid,
      setOptimisticThread,
    ],
  );

  const handleOptimisticThreadCreated = useCallback(
    ({
      threadUid,
      optimisticMessage,
    }: BlockScopedOptimisticThreadCreatedPayload) => {
      const activePendingPost = pendingPostRef.current;
      if (!activePendingPost) {
        return;
      }
      clearComposerFlowState();
      setPendingPost(null);
      setSelectedThreadUid(threadUid);
      setOptimisticThread({
        tempThreadUid: threadUid,
        resolvedThreadUid: null,
        pendingPost: activePendingPost,
        messages: [toMessageData(optimisticMessage)],
      });
    },
    [
      clearComposerFlowState,
      pendingPostRef,
      setPendingPost,
      setSelectedThreadUid,
      setOptimisticThread,
    ],
  );

  const handleThreadCreateFailed = useCallback(
    ({ threadUid, draft, error }: BlockScopedThreadCreateFailedPayload) => {
      const currentOptimisticThread = optimisticThreadRef.current;
      if (
        !currentOptimisticThread ||
        currentOptimisticThread.tempThreadUid !== threadUid
      ) {
        return;
      }
      const shouldRollbackComposer =
        selectedThreadUidRef.current === currentOptimisticThread.tempThreadUid;
      setOptimisticThread(null);
      if (!shouldRollbackComposer) {
        return;
      }
      setSelectedThreadUid(null);
      setPendingPost(currentOptimisticThread.pendingPost);
      setComposerRestoreState({ draft, error });
    },
    [
      optimisticThreadRef,
      selectedThreadUidRef,
      setPendingPost,
      setSelectedThreadUid,
      setOptimisticThread,
    ],
  );

  const handleBootstrapMessagesHydrated = useCallback(
    (threadUid: string) => {
      setOptimisticThread(previous =>
        previous?.resolvedThreadUid === threadUid ? null : previous,
      );
    },
    [setOptimisticThread],
  );

  const selectedThread = selectedThreadUid
    ? threads.find(t => t.uid === selectedThreadUid)
    : null;
  const selectedOptimisticThread = useMemo(() => {
    if (!selectedThreadUid || !optimisticThread) {
      return null;
    }
    if (selectedThreadUid === optimisticThread.tempThreadUid) {
      return optimisticThread;
    }
    if (
      optimisticThread.resolvedThreadUid &&
      selectedThreadUid === optimisticThread.resolvedThreadUid
    ) {
      return optimisticThread;
    }
    return null;
  }, [optimisticThread, selectedThreadUid]);
  const isSelectedTemporaryThread = Boolean(
    selectedOptimisticThread &&
      selectedThreadUid === selectedOptimisticThread.tempThreadUid &&
      !selectedOptimisticThread.resolvedThreadUid,
  );
  const temporaryPostTarget = selectedOptimisticThread
    ? postTargetFromPendingPost(selectedOptimisticThread.pendingPost)
    : undefined;
  const teacherName = leader?.name?.trim() || t("Teacher");
  const showEmptyConversationsState =
    !threadsLoading && threads.length === 0 && !debouncedSearch;

  const sectionInfo = { uid: sectionUid, end_time: sectionEndTime };
  const learnerInfo = {
    uid: currentLearner!.uid,
    name: currentLearner?.name ?? "",
    avatar: currentLearner?.avatar ?? null,
  };
  const viewContext: ThreadViewContext = {
    section: sectionInfo,
    learner: learnerInfo,
    leader: leader ?? null,
  };

  const handleStartThreadFromCurrentPost = () => {
    if (!currentPostContext) {
      return;
    }
    clearComposerFlowState();
    setPendingPost(contextToPendingPost(currentPostContext, "emptyState"));
  };

  return (
    <>
      {isMobile ? (
        <Badge badgeContent={unreadCount} color="error">
          <Button
            variant="contained"
            onClick={() => setOpen(true)}
            sx={{
              minWidth: 0,
              width: 48,
              height: 48,
              borderRadius: "50%",
              padding: 0,
            }}
          >
            <Icon icon={faComment} sx={{ fontSize: "1.5rem" }} />
          </Button>
        </Badge>
      ) : (
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            whiteSpace: "nowrap",
          }}
        >
          <Badge badgeContent={unreadCount} color="error" sx={{ mr: 8 }}>
            <UserHeadshotImage user={leader} size={24} />
          </Badge>
          <Box component="span">{t("Message Teacher")}</Box>
        </Button>
      )}
      <Drawer
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: (theme: Theme) => ({
            width: 400,
            display: "flex",
            flexDirection: "column",
            [theme.breakpoints.down("sm")]: {
              width: "85%",
            },
          }),
        }}
      >
        {selectedThreadUid ? (
          <ThreadDetailView
            threadUid={selectedThreadUid}
            viewContext={viewContext}
            postTitle={postTitleFromThreadOrOptimistic(
              selectedThread,
              selectedOptimisticThread,
            )}
            searchQuery={debouncedSearch}
            optimisticMessages={selectedOptimisticThread?.messages ?? []}
            isTemporaryThread={isSelectedTemporaryThread}
            temporaryPostTarget={temporaryPostTarget}
            onBootstrapMessagesHydrated={handleBootstrapMessagesHydrated}
            onBack={() => setSelectedThreadUid(null)}
            onClose={handleClose}
          />
        ) : pendingPost ? (
          <NewThreadView
            viewContext={viewContext}
            postTarget={postTargetFromPendingPost(pendingPost)}
            postTitle={
              pendingPost.source === "post" ? pendingPost.postTitle : undefined
            }
            hideBackButton={pendingPost.source === "emptyState"}
            onBack={() => {
              setPendingPost(null);
              clearComposerFlowState();
            }}
            onClose={handleClose}
            onThreadCreated={handleThreadCreated}
            onOptimisticThreadCreated={handleOptimisticThreadCreated}
            onThreadCreateFailed={handleThreadCreateFailed}
            initialDraft={composerRestoreState?.draft}
            initialError={composerRestoreState?.error}
          />
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                p: 12,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <UserHeadshotImage
                user={leader}
                size={44}
                sx={{ flexShrink: 0 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                  {leader?.name ?? t("Teacher")}
                </Typography>
                {Boolean(sectionEndTime) && (
                  <Typography variant="caption" color="text.secondary">
                    {t("Teacher access ends {{date}}", {
                      date: dayjs(sectionEndTime).format("MMM D, YYYY"),
                    })}
                  </Typography>
                )}
              </Box>
              <IconButton
                aria-label={t("Close messages")}
                icon={faTimes}
                variant="text"
                onClick={handleClose}
              />
            </Box>
            {!showEmptyConversationsState && (
              <Box
                sx={{
                  py: 8,
                  px: 12,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("Search all messages")}
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon
                          icon={faSearch}
                          sx={{ color: "text.secondary", fontSize: "1rem" }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment:
                      threadsLoading && debouncedSearch ? (
                        <InputAdornment position="end">
                          <CircularProgress size={16} />
                        </InputAdornment>
                      ) : undefined,
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": { borderRadius: 20 },
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
              }}
            >
              {threadsLoading && threads.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    p: 4,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : showEmptyConversationsState ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 24,
                    gap: 12,
                  }}
                >
                  <Icon
                    icon={faComment}
                    sx={{ color: "neutral.300", fontSize: "4rem" }}
                  />
                  <Typography variant="h6" color="text.secondary">
                    {t("No conversations yet")}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", maxWidth: 300 }}
                  >
                    {t(
                      'Explore a lesson and use the "Ask a question" button below your lesson to start chatting with your teacher.',
                    )}
                  </Typography>
                  {currentPostContext && (
                    <Button
                      variant="outlined"
                      onClick={handleStartThreadFromCurrentPost}
                      sx={{
                        mt: 8,
                        borderColor: "neutral.200",
                        color: "text.primary",
                        borderRadius: "1.4rem",
                        textTransform: "none",
                      }}
                    >
                      {t("Message {{teacherName}} directly", {
                        teacherName,
                      })}
                    </Button>
                  )}
                </Box>
              ) : threads.length === 0 ? (
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    p: 2,
                  }}
                >
                  <Typography variant="body1" color="text.secondary">
                    {t("No messages found")}
                  </Typography>
                </Box>
              ) : (
                threads.map(thread => (
                  <ThreadListItem
                    key={thread.uid}
                    thread={thread}
                    onClick={() => setSelectedThreadUid(thread.uid)}
                  />
                ))
              )}
            </Box>
          </>
        )}
      </Drawer>
    </>
  );
}
